import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { cloneDeep, isEqual, set } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { validate } from './fields';
import InputGroup from './InputGroup';
import Discounts from './Discounts';
import CustomFieldFees from './CustomFieldFees';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& .customize-fees-section-label': {
      fontSize: '1rem'
    }
  },
  actionButtons: {
    margin: theme.spacing(0.5)
  }
}));

const Container = ({
  fees,
  originalFees,
  defaultFees,
  onChange,
  onSave,
  discountProps,
  ActionWrapper,
  readOnly
}) => {
  const classes = useStyles();
  const allowReset = defaultFees && Object.keys(defaultFees).length > 0;
  const [dirty, setDirty] = useState(false);
  const [customized, setCustomized] = useState(false);
  const [errors, setErrors] = useState(null);

  const exclude = [];

  const showRxFields = fees.custom_rxform;

  const handleFieldChange = (name, value) => {
    set(fees, name, value);
    onChange({ ...fees });
  };

  const handleReset = () => {
    onChange(cloneDeep(defaultFees));
  };

  const handleCancel = () => {
    onChange(cloneDeep(originalFees));
  };

  useEffect(() => {
    const hasChanged = !isEqual(fees, originalFees);
    setDirty(hasChanged);
  }, [fees, originalFees]);

  useEffect(() => {
    const isCustomized = defaultFees && !isEqual(fees, defaultFees);
    setCustomized(isCustomized);
  }, [fees, defaultFees]);

  useEffect(() => {
    if (!fees) {
      return;
    }

    validate(fees, exclude)
      .then(() => {
        if (fees) {
          setErrors(null);
        }
      })
      .catch(err => {
        if (isEqual(err, errors)) {
          return;
        }
        setErrors(err);
      });
  }, [errors, fees, exclude]);

  const ActionWrapperComponent =
    ActionWrapper ||
    (({ children }) => {
      return children;
    });

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className="customize-fees-section-label" variant="h6">
              Per Arch
            </Typography>
          </Grid>
          <InputGroup
            values={fees}
            errors={errors}
            defaultValues={defaultFees}
            setFieldValue={handleFieldChange}
            name="arch"
            exclude={exclude}
            xs={12}
            sm={12}
            md={6}
            fieldWrapperProps={{ columns: { sm: 1, md: 1 } }}
            readOnly={readOnly}
          />
          <InputGroup
            values={fees}
            errors={errors}
            defaultValues={defaultFees}
            setFieldValue={handleFieldChange}
            name="exports"
            exclude={exclude}
            xs={12}
            sm={12}
            md={6}
            fieldWrapperProps={{ columns: { sm: 1, md: 1 } }}
            readOnly={readOnly}
          />
        </Grid>
      </Grid>
      <InputGroup
        setFieldValue={handleFieldChange}
        values={fees}
        errors={errors}
        defaultValues={defaultFees}
        name="retainer"
        label="Retainers"
        xs={12}
        sm={12}
        md={6}
        fieldWrapperProps={{ columns: { sm: 1, md: 1 } }}
        readOnly={readOnly}
      />
      <InputGroup
        setFieldValue={handleFieldChange}
        values={fees}
        errors={errors}
        defaultValues={defaultFees}
        name="shipping_fees"
        label="Shipping Fees"
        xs={12}
        sm={12}
        md={6}
        fieldWrapperProps={{ columns: { sm: 1, md: 1 } }}
        readOnly={readOnly}
      />
      <Discounts
        discounts={fees.arch_volume_discounts}
        defaultDiscounts={defaultFees && defaultFees.arch_volume_discounts}
        errors={errors}
        xs={12}
        sm={12}
        md={6}
        onChange={value => handleFieldChange('arch_volume_discounts', value)}
        {...discountProps}
        readOnly={readOnly}
      />

      <InputGroup
        errors={errors}
        setFieldValue={handleFieldChange}
        values={fees}
        defaultValues={defaultFees}
        name="settings"
        label="Misc"
        xs={12}
        sm={12}
        md={12}
        fieldWrapperProps={{ columns: { sm: 1, md: 2 } }}
        readOnly={readOnly}
      />

      {showRxFields && (
        <CustomFieldFees
          errors={errors}
          setFieldValue={handleFieldChange}
          values={fees}
          defaultValues={defaultFees}
          readOnly={readOnly}
        />
      )}

      {!readOnly && (
        <Grid item xs={12}>
          <Divider />
          <Box display="flex" justifyContent="flex-end" m={2}>
            <ActionWrapperComponent>
              {allowReset && (
                <Button
                  disabled={!customized}
                  onClick={handleReset}
                  variant="contained"
                  className={classes.actionButtons}
                >
                  Reset Default
                </Button>
              )}
              <>
                <Button
                  disabled={!dirty || !!errors}
                  onClick={onSave}
                  variant="contained"
                  color="primary"
                  className={classes.actionButtons}
                >
                  Save
                </Button>
                <Button
                  disabled={!dirty}
                  onClick={handleCancel}
                  variant="contained"
                  color="secondary"
                  className={classes.actionButtons}
                >
                  Cancel
                </Button>
              </>
            </ActionWrapperComponent>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

Container.defaultProps = {
  defaultFees: {},
  discountProps: {},
  ActionWrapper: null,
  readOnly: false
};

Container.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  fees: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  originalFees: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  defaultFees: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  discountProps: PropTypes.object,
  ActionWrapper: PropTypes.elementType,
  readOnly: PropTypes.bool
};

export default Container;
